/**
 * Format an application size for display.
 *
 * The Smplify backend returns `size` in inconsistent shapes:
 *   - Private (.pkg) apps: pre-formatted strings like "42.4 MB", "3.5 KB"
 *   - Public App Store apps: raw byte-count strings like "634566656"
 *   - Licensed / unknown: null / undefined / ""
 *
 * This helper handles all three cases so the UI never renders a misleading
 * "0.0 KB" from accidentally parsing "42.4 MB" with parseInt.
 */
export function formatAppSize(
  size: string | number | null | undefined,
  emptyPlaceholder: string = "—"
): string {
  if (size === null || size === undefined || size === "") return emptyPlaceholder;

  if (typeof size === "string") {
    const trimmed = size.trim();
    if (!trimmed) return emptyPlaceholder;
    // Already-formatted string from backend (e.g. "42.4 MB", "3.5 KB")
    if (/(kb|mb|gb|tb|bytes|\bb\b)/i.test(trimmed)) {
      return trimmed;
    }
    const parsed = Number(trimmed);
    if (isNaN(parsed)) return trimmed;
    return formatBytes(parsed, emptyPlaceholder);
  }

  return formatBytes(size, emptyPlaceholder);
}

function formatBytes(b: number, emptyPlaceholder: string): string {
  if (!isFinite(b) || b <= 0) return emptyPlaceholder;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
